import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Border, Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { CardProps, HeaderProps } from './consts';

export const Header = styled.div<HeaderProps>`
  font-family: ${Font.Family.SANS};
  font-weight: ${ifProp('isSubHeader', 400, 700)};
  color: ${ifProp('isSubHeader', Colors.GRAY_DARK_10, Colors.GRAY_DARK_30)};
  font-size: ${ifProp('isSubHeader', Font.Size.S, Font.Size.M)};
  letter-spacing: 0;
  width: 100%;
`;

export const HeaderContainer = styled.div<{
  noPadding?: boolean;
  headerPosition: HeaderProps['headerPosition'];
}>`
  margin-bottom: ${Spacing.SINGLE_HALF};
  padding: ${ifProp('noPadding', Spacing.SINGLE_HALF, 0)};
  text-align: ${(props) => props.headerPosition || 'left'};
  width: 100%;
`;

const sharedCardStyles = `
  background: #ffffff;
  border: 1px solid ${Colors.GRAY_LITE_20};
  border-radius: ${Border.Radius.M};
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: ${Spacing.DOUBLE};
`;

const sharedCardSmallStyles = `
  & [data-role='pagination'] {
    padding-left: ${Spacing.SINGLE};
    padding-right: ${Spacing.SINGLE};
  }
`;

export const Container = styled.div<CardProps & { hasHeader?: boolean }>`
  position: relative;

  ${({ noOverflow, noPadding, transparent }) =>
    match({ noOverflow, noPadding, transparent })
      .with(
        { noOverflow: true, noPadding: true },
        () => css`
          ${sharedCardStyles}
          padding: 0;
          overflow: hidden;
        `
      )
      .with(
        { transparent: true },
        () => css`
          background: transparent;
          padding: 0;
        `
      )
      .with(
        { noPadding: true },
        () => css`
          ${sharedCardStyles}
          padding: 0;
        `
      )
      .with(
        { noOverflow: true },
        () => css`
          ${sharedCardStyles}
          overflow: hidden;
        `
      )
      .otherwise(
        () => css`
          ${sharedCardStyles}
          padding: ${Spacing.SINGLE_HALF};
        `
      )};

  :last-child {
    margin-bottom: 0;
  }

  & > * {
    width: 100%;
    box-sizing: border-box;
  }

  @media (max-width: ${ScreenWidth.SMALL}) {
    border-radius: 0;
    ${({ hasHeader, noPadding, noPaddingOnSmallScreen, transparent }) =>
      match({ hasHeader, noPadding, noPaddingOnSmallScreen, transparent })
        .with(
          { transparent: true },
          () =>
            css`
              padding: ${Spacing.SINGLE};

              & ${Header} {
                margin: 0 ${Spacing.SINGLE};
              }
            `
        )
        .with(
          { hasHeader: true, noPaddingOnSmallScreen: true },
          () =>
            css`
              padding: ${Spacing.SINGLE} 0 0 0;
              ${sharedCardSmallStyles}

              & ${Header} {
                margin: 0 ${Spacing.SINGLE};
              }
            `
        )
        .with(
          { noPaddingOnSmallScreen: true },
          () =>
            css`
              padding: 0;
              ${sharedCardSmallStyles}
            `
        )
        .with(
          { noPadding: true },
          () =>
            css`
              padding: 0;
              ${sharedCardSmallStyles}
            `
        )
        .otherwise(
          () => css`
            padding: ${Spacing.SINGLE};
          `
        )}
  }
`;

export const CardContainer = styled.div`
  display: flex;
  gap: ${Spacing.SINGLE_HALF};
  margin: ${Spacing.SINGLE_HALF} 0;

  & > div {
    margin-bottom: 0;
    flex: 1 1 100%;
  }

  @media (max-width: ${ScreenWidth.SMALL}) {
    flex-direction: column;
  }
`;
